import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

const LoadingSpinner = ({ size = 'md', color = 'primary' }) => {
  const { theme } = useTheme();

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const getColorStyle = () => {
    switch (color) {
      case 'primary':
        return theme.colors.primary;
      case 'white':
        return '#ffffff';
      case 'gray':
        return theme.colors.textSecondary;
      default:
        return theme.colors.primary;
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen w-full">
      <motion.div
        className={`${sizeClasses[size]} border-2 rounded-full`}
        style={{
          borderColor: theme.colors.border,
          borderTopColor: getColorStyle()
        }}
        animate={{ rotate: 360 }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }}
      />
    </div>
  );
};

// 页面切换加载组件 - 用于路由懒加载
export const PageTransitionLoader = () => {
  const { theme } = useTheme();

  return (
    <motion.div
      className="flex items-center justify-center h-full min-h-screen w-full"
      style={{ backgroundColor: theme.colors.surface }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      <div className="text-center">
        {/* 双层旋转圆环 */}
        <div className="relative w-16 h-16 mx-auto mb-4">
          {/* 外圈 - 顺时针 */}
          <motion.div
            className="absolute inset-0 rounded-full border-4"
            style={{
              borderColor: theme.colors.border,
              borderTopColor: theme.colors.primary
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          {/* 内圈 - 逆时针 */}
          <motion.div
            className="absolute inset-2 rounded-full border-4"
            style={{
              borderColor: theme.colors.border,
              borderBottomColor: theme.colors.primary
            }}
            animate={{ rotate: -360 }}
            transition={{ duration: 0.75, repeat: Infinity, ease: "linear" }}
          />
        </div>

        {/* 三点跳动动画 */}
        <div className="flex items-center justify-center space-x-1.5 mb-3">
          {[0, 1, 2].map((index) => (
            <motion.div
              key={index}
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: theme.colors.primary }}
              animate={{
                y: [0, -8, 0],
                opacity: [0.4, 1, 0.4],
              }}
              transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: index * 0.15,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>

        <motion.p
          className="text-sm"
          style={{ color: theme.colors.textSecondary }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          页面加载中...
        </motion.p>
      </div>
    </motion.div>
  );
};

// 内联小型加载器 - 用于按钮或小区域
export const InlineLoader = ({ size = 'sm' }) => {
  const { theme } = useTheme();

  const sizeMap = {
    xs: 'w-3 h-3 border',
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
  };

  return (
    <motion.div
      className={`${sizeMap[size]} rounded-full`}
      style={{
        borderColor: theme.colors.border,
        borderTopColor: theme.colors.primary
      }}
      animate={{ rotate: 360 }}
      transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
    />
  );
};

export default LoadingSpinner;
